import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  SimpleGrid,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiFilter, FiPlus, FiSearch } from 'react-icons/fi';
import { useDisclosure } from '@chakra-ui/react';
import { Checkbox } from '@chakra-ui/react';
import { BountyCard } from './BountyCard';
import { useBounties } from '../../hooks/useBounties';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { RESEARCH_TYPES } from '../../utils/formatters';
import { FiInbox } from 'react-icons/fi';

const STORAGE_KEY = 'bountyTypeFilters';

function loadSavedFilters() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return [];
}

export default function BountyList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState(loadSavedFilters);
  const [sortBy, setSortBy] = useState('newest');

  const { isOpen: filtersOpen, onToggle: toggleFilters } = useDisclosure();

  const { bounties, loading, loadingMore, hasMore, loadMore, error } = useBounties();
  const sentinelRef = useRef(null);

  const headerBg = useColorModeValue('white', 'gray.800');
  const filtersActive = activeFilters.length > 0 || searchQuery.trim() || sortBy !== 'newest';

  const toggleFilter = useCallback((key) => {
    setActiveFilters((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const removeFilter = useCallback((key) => {
    setActiveFilters((prev) => {
      const next = prev.filter((k) => k !== key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Infinite scroll: load more when sentinel is visible
  useEffect(() => {
    if (!sentinelRef.current || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0, rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loading, hasMore, loadingMore, loadMore]);

  const hasFilters = activeFilters.length > 0;

  const filtered = bounties
    .filter((b) => !hasFilters || activeFilters.includes(b.researchType))
    .filter((b) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        b.title.toLowerCase().includes(q) ||
        b.description.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }
      if (sortBy === 'highest') return b.rewardAmount - a.rewardAmount;
      if (sortBy === 'lowest') return a.rewardAmount - b.rewardAmount;
      return 0;
    });

  return (
    <Container maxW="7xl" py={6} px={0}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Heading size="lg">Browse Tasks</Heading>
            <Text color="gray.600" mt={1}>
              {loading ? '' : `${filtered.length} open research requests`}
            </Text>
          </Box>
          <HStack spacing={2}>
            <IconButton
              display={{ base: 'flex', md: 'none' }}
              icon={<Icon as={FiFilter} />}
              variant={filtersActive ? 'solid' : 'outline'}
              colorScheme={filtersActive ? 'orange' : 'gray'}
              aria-label="Toggle filters"
              onClick={toggleFilters}
              size="md"
            />
            <Button
              as={Link}
              to="/tasks/create"
              colorScheme="orange"
              leftIcon={<Icon as={FiPlus} />}
              size="md"
              flexShrink={0}
            >
              Post Task
            </Button>
          </HStack>
        </HStack>

        {/* Filters — always visible on desktop, toggleable on mobile */}
        <Box
          display={{ base: filtersOpen ? 'block' : 'none', md: 'block' }}
          bg={headerBg}
          rounded="xl"
          p={4}
          boxShadow="sm"
          borderWidth={1}
          borderColor={useColorModeValue('gray.100', 'gray.700')}
        >
          <VStack spacing={3} align="stretch">
            <HStack spacing={4} flexWrap="wrap" gap={2}>
              <InputGroup maxW={{ base: 'full', md: '300px' }}>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  variant="outline"
                  rightIcon={<Icon as={FiChevronDown} />}
                  size="md"
                  fontWeight="normal"
                  minW="180px"
                  textAlign="left"
                >
                  Filter by category
                </MenuButton>
                <MenuList zIndex={10}>
                  {Object.entries(RESEARCH_TYPES).map(([key, { label }]) => {
                    const isChecked = activeFilters.includes(key);
                    return (
                      <MenuItem
                        key={key}
                        onClick={() => toggleFilter(key)}
                        closeOnSelect={false}
                      >
                        <Checkbox
                          isChecked={isChecked}
                          pointerEvents="none"
                          colorScheme="orange"
                          mr={2}
                        />
                        {label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>

              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                maxW={{ base: 'full', md: '180px' }}
              >
                <option value="newest">Newest first</option>
                <option value="highest">Highest reward</option>
                <option value="lowest">Lowest reward</option>
              </Select>
            </HStack>

            {hasFilters && (
              <HStack spacing={2} align="center" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="semibold" color="gray.500" whiteSpace="nowrap">
                  Category Filters:
                </Text>
                <Wrap spacing={2}>
                  {activeFilters.map((key) => {
                    const type = RESEARCH_TYPES[key];
                    if (!type) return null;
                    return (
                      <WrapItem key={key}>
                        <Tag
                          size="md"
                          colorScheme={type.color}
                          variant="solid"
                          borderRadius="full"
                        >
                          <TagLabel>{type.label}</TagLabel>
                          <TagCloseButton onClick={() => removeFilter(key)} />
                        </Tag>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Grid */}
        {loading ? (
          <LoadingSpinner message="Loading tasks..." />
        ) : error ? (
          <EmptyState
            title="Error loading tasks"
            description={error}
            icon={FiInbox}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No tasks found"
            description={searchQuery || hasFilters
              ? "Try adjusting your filters"
              : "Be the first to post a research task!"}
            icon={FiInbox}
          />
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
              {filtered.map((bounty) => (
                <BountyCard key={bounty.id} bounty={bounty} />
              ))}
            </SimpleGrid>
            {hasMore && <Box ref={sentinelRef} h="1px" />}
            {loadingMore && (
              <HStack justify="center" py={4}>
                <Spinner size="sm" color="orange.500" />
                <Text fontSize="sm" color="gray.500">Loading more tasks...</Text>
              </HStack>
            )}
          </>
        )}
      </VStack>
    </Container>
  );
}
